import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faArrowRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { useMockData } from '../state/MockDataContext';
import { useUpgradeModal, UPSELL_FEATURES } from './UpgradeModal';

// Inline upsell banner reused across the free-tier pages. Clicking the CTA
// opens the UpgradeModal with feature-specific copy. Passing `dismissKey`
// makes the banner closable — the dismissal persists in the demo state so
// upsells don't nag on every visit.
export default function UpsellCard({ feature = 'generic', title, body, ctaLabel, dismissKey }) {
  const { t } = useTranslation('freeSupplier');
  const { openUpgrade } = useUpgradeModal();
  const { data, dismissUpsell } = useMockData();
  const icon = UPSELL_FEATURES[feature]?.icon || faLock;
  const resolvedCtaLabel = ctaLabel || t('upsellCard.seePremiumCta', 'See Premium');

  if (dismissKey && data.dismissedUpsells?.includes(dismissKey)) return null;

  return (
    <div className="relative rounded-2xl border border-primaryColor/25 bg-gradient-to-r from-primaryColor/10 via-primaryColor/5 to-transparent p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-xl bg-primaryColor/15 flex items-center justify-center shrink-0">
        <FontAwesomeIcon
          icon={icon}
          className="text-primaryColor"
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 m-0">{title}</p>
        {body ? <p className="text-xs text-slate-500 m-0 mt-0.5">{body}</p> : null}
      </div>
      <CgRadixButton
        variant="outline"
        size="sm"
        icon={faLock}
        className="shrink-0 bg-white"
        onClick={() => openUpgrade(feature)}
      >
        {resolvedCtaLabel}
        <FontAwesomeIcon
          icon={faArrowRight}
          className="text-[10px]"
        />
      </CgRadixButton>
      {dismissKey && (
        <button
          type="button"
          onClick={() => dismissUpsell(dismissKey)}
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-600 flex items-center justify-center shadow-sm"
          title={t('upsellCard.dismissTitle', 'Dismiss')}
        >
          <FontAwesomeIcon
            icon={faXmark}
            className="text-[10px]"
          />
        </button>
      )}
    </div>
  );
}
